/**
 * The shape of a published docs artifact, and the lookup logic over it.
 *
 * There are TWO artifacts (see context/scripts/publish-guides.mjs): the customer one at
 * `generated/search-index.json`, and the internal one at `generated/internal-search-index.json`
 * that /internal/docs renders. They are produced by the same publisher from the same
 * frontmatter, so they have the same shape — which means the lookup, ordering and
 * neighbour logic is identical and belongs in one factory rather than in two modules that
 * would drift the first time either side gained a feature.
 *
 * `docs/index.ts` binds this to the customer artifact and keeps its historical named
 * exports, so nothing that imported `DOCS` / `findDoc` had to change. `docs/internal.ts`
 * binds the same factory to the internal artifact.
 */
import { searchDocEntries } from 'search/scoring'

/** A heading in a published body, with the anchor assigned at publish time. */
export interface DocHeading {
  /** Slugified, deduplicated, stable — the `#fragment` in /docs/:category/:slug#id. */
  id: string
  text: string
  /** 2–6. The leading H1 is lifted into `headline`, so bodies start at depth 2. */
  depth: number
  /**
   * 1-based line of this heading within the published body. The renderer reads the same
   * line off each heading node and uses it to stamp `id` onto the rendered element —
   * see DocContent. Re-slugifying the text in the browser instead would be a second
   * implementation of the same rule, and the two would eventually disagree.
   */
  line: number
}

export interface DocEntry {
  slug: string
  category: string
  /** Short label, for nav rows and index cards. */
  title: string
  /** Full page title (the guide's H1). Usually more descriptive than `title`. */
  headline: string
  tags: string[]
  lastReviewed: string
  order: number
  excerpt: string
  headings: DocHeading[]
  /**
   * VRG-680 — the launch switch this guide is gated on (`feature:` frontmatter), absent on
   * every ungated guide. Applied at run time by `docs/featureGate.ts`.
   */
  feature?: string
}

export interface DocCategory {
  id: string
  label: string
}

const DAY_MS = 86_400_000

/** Days since a guide was last reviewed, or null if it never declared a date. */
export const daysSinceReview = (doc: DocEntry): number | null => {
  const t = Date.parse(doc.lastReviewed)
  return Number.isNaN(t) ? null : Math.floor((Date.now() - t) / DAY_MS)
}

/** Matches STALE_AFTER_DAYS in publish-guides.mjs. */
const STALE_AFTER_DAYS = 180

/**
 * Unreviewed for a long time. Surfaced, not hidden — unverified is not wrong.
 *
 * Derived here rather than read off the artifact. It used to be a baked-in field, which
 * meant the generated file changed every day the date advanced and `publish-guides.mjs
 * --check` declared it stale about 24 hours after every publish, with no guide having
 * changed. A freshness check that cries wolf daily is one everybody learns to skip. The
 * artifact now carries only `lastReviewed`, which is a fact about the guide, so it is a
 * pure function of the guides and the check means something again (VRG-227).
 */
export const isStale = (doc: DocEntry): boolean => {
  const age = daysSinceReview(doc)
  return age !== null && age > STALE_AFTER_DAYS
}

/** The manifest as the publisher writes it. */
interface DocsManifest {
  guideCount: number
  categories: DocCategory[]
  docs: DocEntry[]
}

export interface DocsCatalog {
  /** Categories that actually hold a guide, in the artifact's editorial order. */
  categories: DocCategory[]
  /** Every guide in the artifact, ordered by category, then order, then title. */
  docs: DocEntry[]
  findDoc: (category?: string, slug?: string) => DocEntry | undefined
  categoryLabel: (id: string) => string
  docsInCategory: (id: string) => DocEntry[]
  neighbours: (doc: DocEntry) => { prev?: DocEntry; next?: DocEntry }
  search: (query: string) => DocEntry[]
}

const key = (category: string, slug: string) => `${category}/${slug}`

export const createDocsCatalog = (manifest: unknown): DocsCatalog => {
  const data = manifest as unknown as DocsManifest
  const categories = data.categories
  const docs = data.docs
  const byKey = new Map(docs.map(d => [key(d.category, d.slug), d]))

  return {
    categories,
    docs,

    findDoc: (category, slug) => (category && slug ? byKey.get(key(category, slug)) : undefined),

    categoryLabel: id => categories.find(c => c.id === id)?.label ?? id,

    docsInCategory: id => docs.filter(d => d.category === id),

    /**
     * Previous/next across the WHOLE ordered set, not just within a category.
     *
     * The docs are one sequence a reader can walk end to end; stopping the chain at a
     * category boundary would strand the last guide in every category with a dead "next".
     * The section nav already communicates which category you're in, so crossing one here
     * reads as progress rather than as a jump.
     */
    neighbours: doc => {
      const i = docs.findIndex(d => d.category === doc.category && d.slug === doc.slug)
      if (i === -1) return {}
      return { prev: docs[i - 1], next: docs[i + 1] }
    },

    /**
     * Filter for the docs index page.
     *
     * VRG-227 replaced the tiered substring matcher that used to live here with a call
     * into `search/scoring`, which is now the only ranking implementation in the app. The
     * old one was explicitly a placeholder for this; keeping it would have meant /docs and
     * the ⌘K palette ranking the same query differently, which is the specific failure the
     * scorer's single-module rule exists to prevent.
     *
     * The signature is a page-level, deduplicated list. The palette calls
     * `searchDocEntries` directly instead, because it wants the anchor-level rows this
     * view has nowhere to put.
     */
    search: query => {
      // An empty query means "show me everything", not "nothing matched" — the index page
      // renders its full grouped catalog through this same call.
      if (!query.trim()) return docs

      const seen = new Set<string>()
      const ordered: DocEntry[] = []
      // No limit: this is a page-wide filter, not a palette's top-N list.
      for (const match of searchDocEntries(docs, query, Number.MAX_SAFE_INTEGER)) {
        const k = key(match.doc.category, match.doc.slug)
        if (seen.has(k)) continue
        seen.add(k)
        ordered.push(match.doc)
      }
      return ordered
    },
  }
}
